// FlowQA ITP v2 — row-and-column grid model (Phase 3)
export const MAX_COLS = 12;
export const MAX_COLS_MSG = "This ITP table can contain a maximum of 12 columns.";

export const CELL_TYPE_GROUPS = [
  ["Content", [["static_text", "Static Text"], ["heading", "Heading"]]],
  ["Inputs", [["short_text", "Short Text"], ["long_text", "Long Text"], ["number", "Number"],
    ["date", "Date"], ["datetime", "Date & Time"], ["dropdown", "Dropdown"],
    ["checkbox", "Checkbox"], ["yes_no", "Yes / No"]]],
  ["Inspection", [["inspection_point", "Point Type"], ["inspection_result", "Inspection Result"],
    ["acceptance_criteria", "Acceptance Criteria"], ["spec_reference", "Specification Reference"],
    ["frequency", "Frequency"], ["verification_record", "Verification Record"],
    ["responsible_party", "Responsible Party"], ["comments", "Comments"], ["status", "Status"]]],
  ["Evidence & Signatures", [["evidence", "Evidence"], ["attachment", "Attachment"],
    ["contractor_signature", "Contractor Signature"], ["client_signature", "Client Signature"],
    ["superintendent_signature", "Superintendent Signature"], ["approval_decision", "Approval Decision"]]],
  ["Auto Fields", [["lot_number_auto", "Lot Number (auto)"], ["project_name", "Project Name"],
    ["project_code", "Project Code"], ["itp_number", "ITP Number"], ["template_revision", "Template Revision"],
    ["user_name", "User Name"], ["user_role", "User Role"], ["company_name", "Company Name"]]],
];
export const CELL_TYPES = CELL_TYPE_GROUPS.flatMap(([, l]) => l);
export const CELL_TYPE_LABELS = Object.fromEntries(CELL_TYPES);
export const STATIC_TYPES = new Set(["static_text", "heading"]);

export const ROW_TYPES = [
  ["header", "Header"], ["section", "Section"], ["item", "Inspection"],
  ["instruction", "Notes"], ["spacer", "Spacer"],
];
export const ROW_TYPE_LABELS = Object.fromEntries(ROW_TYPES);

// Simple user-facing column types — each drives cell behaviour internally.
export const COLUMN_TYPES = [
  ["static_text", "Static Text"], ["editable_text", "Editable Text"], ["long_text", "Long Text"],
  ["date", "Date"], ["dropdown", "Dropdown"], ["checkbox", "Checkbox"], ["status", "Status"],
  ["evidence", "Evidence"], ["comments", "Comments"], ["point_type", "Point Type"],
  ["contractor_signature", "Contractor Signature"], ["client_signature", "Client Signature"],
  ["superintendent_signature", "Superintendent Signature"], ["other", "Other"],
];
export const COLUMN_TYPE_LABELS = Object.fromEntries(COLUMN_TYPES);
const COL_CELL = {
  static_text: "static_text", editable_text: "short_text", long_text: "long_text",
  date: "date", dropdown: "dropdown", checkbox: "checkbox", status: "status",
  evidence: "evidence", comments: "comments", point_type: "inspection_point",
  contractor_signature: "contractor_signature", client_signature: "client_signature",
  superintendent_signature: "superintendent_signature", other: "short_text",
};
const COL_ROLE = {
  point_type: "inspection_point", contractor_signature: "contractor_signoff",
  client_signature: "client_signoff", superintendent_signature: "superintendent_signoff",
};
export const cellTypeForColumn = (col) => COL_CELL[col?.col_type] || "static_text";

export const FUNCTIONAL_ROLES = [
  ["inspection_point", "Inspection Point"], ["order_reference", "Order / Reference"],
  ["activity_description", "Activity Description"], ["applicable_standard", "Applicable Standard"],
  ["acceptance_criteria", "Acceptance Criteria"], ["frequency", "Frequency"],
  ["verification_records", "Verification Records"], ["responsible_party", "Responsible Party"],
  ["result", "Inspection Result"], ["evidence", "Evidence"], ["comments", "Comments"],
  ["contractor_signoff", "Contractor Sign-off"], ["client_signoff", "Client Sign-off"],
  ["superintendent_signoff", "Superintendent Sign-off"], ["completion_date", "Completion Date"],
  ["lot_number", "Lot Number"], ["project_name", "Project Name"],
  ["template_revision", "Template Revision"], ["spec_reference", "Spec Reference"],
];

export const POINT_TYPES = [
  ["HP", "HP — Hold Point", true], ["AP", "AP — Action Point", true],
  ["WP", "WP — Witness Point", false], ["R", "R — Review", false], ["S", "S — Surveillance", false],
];

export const ROW_RULE_FLAGS = [
  ["evidence_required", "Evidence required"],
  ["contractor_signoff_required", "Contractor sign-off required"],
  ["client_signoff_required", "Client sign-off required"],
  ["superintendent_signoff_required", "Superintendent sign-off required"],
  ["mandatory_comments", "Mandatory comments"],
  ["external_release_required", "External release required"],
  ["blocks_completion", "Blocks ITP completion"],
  ["blocks_lot_closeout", "Blocks lot close-out"],
];

const uid = () => crypto.randomUUID();

export const newCell = (colId, patch = {}) => ({
  id: uid(), start_column_id: colId, column_span: 1, row_span: 1,
  type: "static_text", value: "", ...patch,
});

export const newRow = (columns, row_type = "item", cellPatches = []) => ({
  id: uid(), row_type, height: "auto", repeat_on_page_break: row_type === "header",
  rules: {}, cells: columns.map((c, i) => newCell(c.id, cellPatches[i] || {})),
});

// Row creation that honours each column's simple Column Type.
export const newRowTyped = (columns, row_type = "item") => {
  if (["section", "instruction", "spacer"].includes(row_type)) {
    return {
      id: uid(), row_type, height: "auto", repeat_on_page_break: false, rules: {},
      cells: [newCell(columns[0].id, {
        column_span: columns.length,
        ...(row_type === "section" ? { bold: true, shading: "#EEEEEE", value: "Section heading" } : {}),
        ...(row_type === "instruction" ? { value: "Notes" } : {}),
      })],
    };
  }
  if (row_type === "header") {
    return {
      id: uid(), row_type, height: "auto", repeat_on_page_break: true, rules: {},
      cells: columns.map((c) => newCell(c.id, { value: c.label || "", shading: "#D9D9D9", bold: true })),
    };
  }
  return {
    id: uid(), row_type, height: "auto", repeat_on_page_break: false, rules: {},
    cells: columns.map((c) => newCell(c.id, { type: cellTypeForColumn(c) })),
  };
};

// Set a column's simple type; retypes the inspection-row cells in that column.
export function applyColumnType(config, colIdx, colType) {
  const columns = config.columns.map((c, i) => (i === colIdx
    ? { ...c, col_type: colType, functional_role: COL_ROLE[colType] || null } : c));
  const cellType = COL_CELL[colType] || "static_text";
  const rows = config.rows.map((r) => {
    if (r.row_type !== "item") return r;
    const j = cellIndexAt(r, colIdx);
    if (j < 0 || (r.cells[j].column_span || 1) > 1) return r;
    return { ...r, cells: r.cells.map((c, jj) => (jj === j ? { ...c, type: cellType } : c)) };
  });
  return { ...config, columns, rows };
}

export function blankTable(nCols = 4, label = "ITP Table") {
  const n = Math.max(1, Math.min(nCols, MAX_COLS));
  const w = Math.round((100 / n) * 100) / 100;
  const columns = Array.from({ length: n }, (_, i) => ({ id: uid(), label: `Column ${i + 1}`, width: w, col_type: "static_text" }));
  return { label, columns, rows: [newRowTyped(columns, "header"), newRowTyped(columns, "item"), newRowTyped(columns, "item")] };
}

// --- layout helpers -------------------------------------------------------
export const cellStarts = (row) => {
  let p = 0;
  return row.cells.map((c) => { const s = p; p += c.column_span || 1; return s; });
};
export const cellIndexAt = (row, colIdx) => {
  let p = 0;
  for (let j = 0; j < row.cells.length; j++) {
    const span = row.cells[j].column_span || 1;
    if (colIdx >= p && colIdx < p + span) return j;
    p += span;
  }
  return -1;
};

// Re-establish spans, start columns and vertical-merge integrity. Returns new config.
export function normalize(config) {
  const colIds = config.columns.map((c) => c.id);
  const n = colIds.length;
  const rows = config.rows.map((r) => {
    let used = 0; const cells = [];
    for (const c0 of r.cells) {
      if (used >= n) break;
      const span = Math.max(1, Math.min(c0.column_span || 1, n - used));
      cells.push({ ...c0, column_span: span, start_column_id: colIds[used], merged_above: false });
      used += span;
    }
    while (used < n) { cells.push(newCell(colIds[used])); used += 1; }
    return { ...r, cells };
  });
  const starts = rows.map(cellStarts);
  for (let i = 0; i < rows.length; i++) {
    for (let j = 0; j < rows[i].cells.length; j++) {
      const c = rows[i].cells[j];
      let want = Math.min(c.row_span || 1, rows.length - i);
      if (c.merged_above) continue;
      if (want <= 1) { if (c.row_span !== 1) rows[i].cells[j] = { ...c, row_span: 1 }; continue; }
      const a = starts[i][j], b = a + c.column_span;
      let achieved = 1;
      for (let k = 1; k < want; k++) {
        const rr = rows[i + k];
        const idxs = [];
        let cover = 0;
        for (let jj = 0; jj < rr.cells.length; jj++) {
          const s = starts[i + k][jj], cc = rr.cells[jj];
          if (s >= a && s + cc.column_span <= b) { idxs.push(jj); cover += cc.column_span; }
          else if (s < b && s + cc.column_span > a) { cover = -1; break; }
        }
        if (cover !== b - a) break;
        idxs.forEach((jj) => { rr.cells[jj] = { ...rr.cells[jj], merged_above: true, row_span: 1 }; });
        achieved = k + 1;
      }
      if (achieved !== (c.row_span || 1)) rows[i].cells[j] = { ...c, row_span: achieved };
    }
  }
  return { ...config, rows };
}

// --- column operations ----------------------------------------------------
export function addColumn(config, atIdx) {
  if (config.columns.length >= MAX_COLS) return { error: MAX_COLS_MSG };
  const avg = config.columns.reduce((s, c) => s + (c.width || 1), 0) / config.columns.length;
  const col = { id: uid(), label: `Column ${config.columns.length + 1}`, width: Math.round(avg * 100) / 100, col_type: "static_text" };
  const columns = [...config.columns];
  columns.splice(atIdx, 0, col);
  const cellFor = (r) => r.row_type === "header"
    ? newCell(col.id, { value: col.label, shading: "#D9D9D9", bold: true })
    : newCell(col.id);
  const rows = config.rows.map((r) => {
    let pos = 0, inserted = false;
    const cells = [];
    for (const c of r.cells) {
      if (!inserted && atIdx === pos) { cells.push(cellFor(r)); inserted = true; cells.push(c); }
      else if (!inserted && atIdx > pos && atIdx < pos + (c.column_span || 1)) {
        cells.push({ ...c, column_span: (c.column_span || 1) + 1 }); inserted = true;
      } else cells.push(c);
      pos += c.column_span || 1;
    }
    if (!inserted) cells.push(cellFor(r));
    return { ...r, cells };
  });
  return normalize({ ...config, columns, rows });
}

export function deleteColumn(config, colIdx) {
  if (config.columns.length <= 1) return { error: "An ITP table needs at least one column." };
  const columns = config.columns.filter((_, i) => i !== colIdx);
  const rows = config.rows.map((r) => {
    const j = cellIndexAt(r, colIdx);
    const cells = r.cells.flatMap((c, jj) => {
      if (jj !== j) return [c];
      return (c.column_span || 1) > 1 ? [{ ...c, column_span: c.column_span - 1 }] : [];
    });
    return { ...r, cells };
  });
  return normalize({ ...config, columns, rows });
}

export function moveColumn(config, colIdx, dir) {
  const target = colIdx + dir;
  if (target < 0 || target >= config.columns.length) return config;
  for (const r of config.rows) {
    const a = cellIndexAt(r, colIdx), b = cellIndexAt(r, target);
    if (a === b || (r.cells[a].column_span || 1) !== 1 || (r.cells[b].column_span || 1) !== 1)
      return { error: "Split merged cells in these columns before reordering." };
  }
  const columns = [...config.columns];
  [columns[colIdx], columns[target]] = [columns[target], columns[colIdx]];
  const rows = config.rows.map((r) => {
    const a = cellIndexAt(r, colIdx), b = cellIndexAt(r, target);
    const cells = [...r.cells];
    [cells[a], cells[b]] = [cells[b], cells[a]];
    return { ...r, cells };
  });
  return normalize({ ...config, columns, rows });
}

export function setColumn(config, colIdx, patch) {
  const columns = config.columns.map((c, i) => (i === colIdx ? { ...c, ...patch } : c));
  return { ...config, columns };
}

// Rename a column heading — header rows mirror the heading automatically.
export function renameColumn(config, colIdx, label) {
  const next = setColumn(config, colIdx, { label });
  const rows = next.rows.map((r) => {
    if (r.row_type !== "header") return r;
    const j = cellIndexAt(r, colIdx);
    if (j < 0 || (r.cells[j].column_span || 1) > 1) return r;
    return { ...r, cells: r.cells.map((c, jj) => (jj === j ? { ...c, value: label } : c)) };
  });
  return { ...next, rows };
}

export function resizeColumn(config, colIdx, delta) {
  if (colIdx >= config.columns.length - 1) return config;
  const a = config.columns[colIdx], b = config.columns[colIdx + 1];
  const na = Math.max(1.5, (a.width || 1) + delta);
  const nb = Math.max(1.5, (b.width || 1) - (na - (a.width || 1)));
  const columns = config.columns.map((c, i) =>
    i === colIdx ? { ...c, width: Math.round(na * 100) / 100 }
      : i === colIdx + 1 ? { ...c, width: Math.round(nb * 100) / 100 } : c);
  return { ...config, columns };
}

// --- row operations -------------------------------------------------------
export function addRow(config, atIdx, row_type = "item") {
  const rows = [...config.rows];
  rows.splice(atIdx, 0, newRowTyped(config.columns, row_type));
  return normalize({ ...config, rows });
}
export function deleteRow(config, rowIdx) {
  if (config.rows.length <= 1) return { error: "A table needs at least one row." };
  return normalize({ ...config, rows: config.rows.filter((_, i) => i !== rowIdx) });
}
export function duplicateRow(config, rowIdx) {
  const src = config.rows[rowIdx];
  const copy = { ...structuredClone(src), id: uid() };
  copy.cells = copy.cells.map((c) => ({ ...c, id: uid(), row_span: 1, merged_above: false }));
  const rows = [...config.rows];
  rows.splice(rowIdx + 1, 0, copy);
  return normalize({ ...config, rows });
}
export function moveRow(config, rowIdx, dir) {
  const t = rowIdx + dir;
  if (t < 0 || t >= config.rows.length) return config;
  const rows = [...config.rows];
  [rows[rowIdx], rows[t]] = [rows[t], rows[rowIdx]];
  return normalize({ ...config, rows });
}
export function setRow(config, rowIdx, patch) {
  const rows = config.rows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r));
  return { ...config, rows };
}

// --- cell operations ------------------------------------------------------
export function setCell(config, rowIdx, cellIdx, patch) {
  const rows = config.rows.map((r, i) => {
    if (i !== rowIdx) return r;
    return { ...r, cells: r.cells.map((c, j) => (j === cellIdx ? { ...c, ...patch } : c)) };
  });
  return { ...config, rows };
}

export function mergeRight(config, rowIdx, cellIdx) {
  const row = config.rows[rowIdx];
  const a = row.cells[cellIdx], b = row.cells[cellIdx + 1];
  if (!b) return { error: "No cell to the right to merge with." };
  if (a.merged_above || b.merged_above || (a.row_span || 1) > 1 || (b.row_span || 1) > 1)
    return { error: "Split vertical merges before merging horizontally." };
  const value = [a.value, b.value].filter((v) => (v || "").trim()).join(" ");
  const cells = row.cells.flatMap((c, j) =>
    j === cellIdx ? [{ ...a, value, column_span: (a.column_span || 1) + (b.column_span || 1) }]
      : j === cellIdx + 1 ? [] : [c]);
  return normalize(setRowCells(config, rowIdx, cells));
}

export function mergeDown(config, rowIdx, cellIdx) {
  const cell = config.rows[rowIdx].cells[cellIdx];
  if (cell.merged_above) return { error: "This cell is part of a vertical merge already." };
  const next = normalize(setCell(config, rowIdx, cellIdx, { row_span: (cell.row_span || 1) + 1 }));
  const after = next.rows[rowIdx].cells[cellIdx];
  if ((after.row_span || 1) <= (cell.row_span || 1))
    return { error: "Cells below must align with this cell before merging down." };
  return next;
}

export function splitCell(config, rowIdx, cellIdx) {
  const row = config.rows[rowIdx];
  const cell = row.cells[cellIdx];
  const starts = cellStarts(row);
  const start = starts[cellIdx];
  const pieces = Array.from({ length: cell.column_span || 1 }, (_, k) =>
    k === 0 ? { ...cell, column_span: 1, row_span: 1 } : newCell(config.columns[start + k]?.id));
  const cells = row.cells.flatMap((c, j) => (j === cellIdx ? pieces : [c]));
  return normalize(setRowCells(config, rowIdx, cells));
}

const setRowCells = (config, rowIdx, cells) => ({
  ...config,
  rows: config.rows.map((r, i) => (i === rowIdx ? { ...r, cells } : r)),
});

// --- presets --------------------------------------------------------------
const HDR = { shading: "#D9D9D9", bold: true };

export function presetMainInspection() {
  const defs = [
    ["HP/AP", 5, "inspection_point"], ["Order", 5, "order_reference"],
    ["Activity or Description", 15, "activity_description"], ["Applicable Standard", 10, "applicable_standard"],
    ["Acceptance Criteria", 18, "acceptance_criteria"], ["Frequency", 9, "frequency"],
    ["Verification Records", 10, "verification_records"], ["Action Responsibility", 9, "responsible_party"],
    ["Sign & Date — Contractor", 9.5, "contractor_signoff"], ["Sign & Date — Client", 9.5, "client_signoff"],
  ];
  const columns = defs.map(([label, width, functional_role]) => ({ id: uid(), label, width, functional_role }));
  const header = newRow(columns, "header", defs.map(([label]) => ({ value: label, ...HDR })));
  const section = newRow(columns, "section", [{ value: "" }, { value: "1" }, { value: "Pre-construction", bold: true }]);
  const item = newRow(columns, "item", [
    { type: "inspection_point" }, { value: "1.1" }, { value: "" }, { value: "" }, { value: "" },
    { value: "" }, { value: "" }, { value: "" },
    { type: "contractor_signature" }, { type: "client_signature" },
  ]);
  return { label: "Main Inspection Table", columns, rows: [header, section, item] };
}

export function presetProjectInfo() {
  const widths = [14, 22, 14, 14, 14, 22];
  const columns = widths.map((w) => ({ id: uid(), label: "", width: w }));
  const L = (v) => ({ value: v, bold: true, shading: "#F2F2F2" });
  const r1 = newRow(columns, "item", [L("Project:"), { type: "project_name", column_span: 3 }, L("ITP No:"), { type: "itp_number" }]);
  r1.cells = r1.cells.slice(0, 5);
  const r2 = newRow(columns, "item", [L("Reference:"), { type: "short_text" }, L("Lot Date:"), { type: "date" }, L("Rev:"), { type: "template_revision" }]);
  const r3 = newRow(columns, "item", [L("Lot No:"), { type: "lot_number_auto", functional_role: "lot_number", column_span: 5 }]);
  r3.cells = r3.cells.slice(0, 2);
  const r4 = newRow(columns, "item", [L("Lot Description:"), { type: "long_text", column_span: 5 }]);
  r4.cells = r4.cells.slice(0, 2);
  const r5 = newRow(columns, "item", [L("Chainage From:"), { type: "short_text" }, L("Chainage To:"), { type: "short_text" }, L("Width / Zone:"), { type: "short_text" }]);
  return normalize({ label: "Project & Document Information", columns, rows: [r1, r2, r3, r4, r5] });
}

export function presetLotConformance() {
  const columns = [{ id: uid(), label: "", width: 25 }, { id: uid(), label: "", width: 37 }, { id: uid(), label: "", width: 38 }];
  const L = (v) => ({ value: v, bold: true, shading: "#F2F2F2" });
  const rows = [
    newRow(columns, "item", [L("Lot creator:"), { type: "short_text", placeholder: "Name" }, { type: "short_text", placeholder: "Title / role" }]),
    newRow(columns, "item", [L("Parent / underlying lot:"), { type: "short_text" }, { type: "date", placeholder: "Date works completed" }]),
    newRow(columns, "item", [L("Lot fully conforms:"), { type: "yes_no", placeholder: "Yes / Subject to NCR" }, { type: "date", placeholder: "Date lot closed" }]),
    newRow(columns, "signature", [L("Verifier / signatory:"), { type: "short_text", placeholder: "Name" }, { type: "contractor_signature" }]),
  ];
  return { label: "Lot Conformance", columns, rows };
}

export function presetSupportingDocs() {
  const columns = [
    { id: uid(), label: "Supporting Documents", width: 30 },
    { id: uid(), label: "Required", width: 12 },
    { id: uid(), label: "Attached", width: 12 },
    { id: uid(), label: "Details/Comments", width: 46 },
  ];
  const title = newRow(columns, "section", [{ value: "Supporting Documents", column_span: 4, bold: true, shading: "#D9D9D9", align: "center" }]);
  title.cells = title.cells.slice(0, 1);
  const header = newRow(columns, "header", columns.map((c) => ({ value: c.label, ...HDR })));
  const docs = ["Lot Plan / Location Map", "Site Photos", "Test Results", "Survey Report", "NCR Closed",
    "RFI Approved", "Mix Design", "Methodology", "Delivery Dockets", "Other"];
  const rows = docs.map((d) => newRow(columns, "item", [{ value: d }, { type: "yes_no" }, { type: "yes_no" }, { type: "comments" }]));
  return normalize({ label: "Supporting Documents", columns, rows: [title, header, ...rows] });
}
